import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchProjectById } from '../../fetchProjects';
import { sanitizeAndFormat } from '../../utils/sanitize';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProject = async () => {
      setLoading(true);
      const data = await fetchProjectById(id);
      setProject(data);
      setLoading(false);
    };
    loadProject();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background text-text-primary flex flex-col items-center justify-center p-4">
        <Helmet>
          <meta name="robots" content="noindex" />
          <title>Project Not Found | Sudhan R</title>
        </Helmet>
        <h2 className="text-2xl font-bold mb-4 font-heading">Project Not Found</h2>
        <Link to="/projects">
          <Button variant="primary">Back to Projects</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent text-text-primary selection:bg-accent selection:text-white">
      <Helmet>
        <title>{project.title} | Case Study | Sudhan R</title>
        <meta name="description" content={project.description} />
        <meta name="keywords" content={project.toolsUsed?.join(", ") + ", Sudhan R, Case Study"} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        
        <meta property="og:title" content={`${project.title} | Technical Case Study`} />
        <meta property="og:description" content={project.description} />
        <meta property="og:image" content={project.image || "https://sudhan.live/assets/images/Sudhan_2026_v3.png"} />
        <meta property="og:type" content="article" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${project.title} | Technical Case Study`} />
        <meta name="twitter:description" content={project.description} />
        <meta name="twitter:image" content={project.image || "https://sudhan.live/assets/images/Sudhan_2026_v3.png"} />

        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "${project.codeRepository ? 'SoftwareSourceCode' : 'CreativeWork'}",
                "@id": "https://sudhan.live/projects/${project.slug || id}#project",
                "name": "${project.title.replace(/"/g, '\\"')}",
                "description": "${project.description.replace(/"/g, '\\"')}",
                "image": "${project.image || 'https://sudhan.live/assets/images/Sudhan_2026_v3.png'}",
                "author": {
                  "@id": "https://sudhan.live/#person"
                },
                "publisher": {
                  "@id": "https://sudhan.live/#person"
                },
                "dateCreated": "${project.completionDate}",
                "keywords": "${project.toolsUsed?.join(", ")}",
                ${project.codeRepository ? `"codeRepository": "${project.codeRepository}",` : ''}
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": "https://sudhan.live/projects/${project.slug || id}"
                }
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://sudhan.live/projects/${project.slug || id}#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://sudhan.live/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Projects",
                    "item": "https://sudhan.live/projects"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "${project.title.replace(/"/g, '\\"')}",
                    "item": "https://sudhan.live/projects/${project.slug || id}"
                  }
                ]
              }
            ]
          }
          `}
        </script>
      </Helmet>
      
      <Header />

      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Back Button */}
          <button 
            onClick={() => {
              navigate(-1);
            }}
            title="Return to the Projects Overview page"
            aria-label="Navigate back to projects portfolio"
            className="inline-flex items-center text-text-secondary hover:text-text-primary transition-colors mb-8 group"
          >
            <Icon name="ArrowLeft" size={18} className="mr-2 transform group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Projects</span>
          </button>

          {/* Main Card Container */}
          <div className="bg-surface/50 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden shadow-2xl">
            
            {/* Project Hero Image */}
            <div className="relative h-[300px] sm:h-[450px]">
              <Image 
                src={project.image} 
                alt={project.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent"></div>
            </div>

            {/* Content Section */}
            <div className="px-6 py-8 sm:px-12 sm:pb-12 -mt-20 relative z-10">
              
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="px-3 py-1 bg-accent/10 text-accent rounded-full text-xs font-semibold uppercase tracking-wider border border-accent/20">
                  {project.type}
                </span>
                {project.industry?.map((ind, idx) => (
                  <span key={idx} className="px-3 py-1 bg-white/5 text-text-secondary rounded-full text-xs font-semibold border border-white/10">
                    {ind}
                  </span>
                ))}
                {project.featured === 1 && (
                  <span className="px-3 py-1 bg-warning/10 text-warning rounded-full text-xs font-semibold flex items-center gap-1 border border-warning/20">
                    <Icon name="Star" size={12} className="fill-current" />
                    Featured Project
                  </span>
                )}
                <span className="text-text-muted text-xs flex items-center gap-1 font-medium">
                  <Icon name="Calendar" size={12} />
                  {project.completionDate}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-4xl sm:text-6xl font-black mb-8 text-text-primary uppercase leading-tight font-heading">
                {project.title}
              </h1>

              {/* Meta Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
                {/* Team / Author */}
                <div className="group relative bg-surface/40 backdrop-blur-xl rounded-2xl p-6 border border-white/5 hover:border-accent/30 hover:bg-surface/60 transition-all duration-300 hover:shadow-[0_8px_30px_rgba(0,195,255,0.08)] flex gap-5 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10 p-3.5 bg-[#1E293B]/80 rounded-xl h-fit border border-white/10 shadow-inner group-hover:bg-accent/20 group-hover:border-accent/30 group-hover:scale-105 transition-all duration-300">
                    <Icon name="Users" size={22} className="text-accent group-hover:text-accent-light drop-shadow-[0_0_10px_rgba(0,195,255,0.4)] transition-colors" />
                  </div>
                  <div className="relative z-10 flex flex-col justify-center">
                    <h3 className="text-[11px] font-black text-text-muted mb-1.5 tracking-[0.2em] font-heading group-hover:text-accent/80 transition-colors uppercase">Team / Author</h3>
                    <p className="text-base font-bold text-text-primary leading-tight">
                      {project.team?.length > 0 ? [project.author, ...project.team].join(', ') : project.author}
                    </p>
                  </div>
                </div>

                {/* Client / Organization */}
                {project.client && (
                <div className="group relative bg-surface/40 backdrop-blur-xl rounded-2xl p-6 border border-white/5 hover:border-accent/30 hover:bg-surface/60 transition-all duration-300 hover:shadow-[0_8px_30px_rgba(0,195,255,0.08)] flex gap-5 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10 p-3.5 bg-[#1E293B]/80 rounded-xl h-fit border border-white/10 shadow-inner group-hover:bg-accent/20 group-hover:border-accent/30 group-hover:scale-105 transition-all duration-300">
                    <Icon name="Building" size={22} className="text-accent group-hover:text-accent-light drop-shadow-[0_0_10px_rgba(0,195,255,0.4)] transition-colors" />
                  </div>
                  <div className="relative z-10 flex flex-col justify-center">
                    <h3 className="text-[11px] font-black text-text-muted mb-1.5 tracking-[0.2em] font-heading group-hover:text-accent/80 transition-colors uppercase">Client / Organization</h3>
                    <p className="text-base font-bold text-text-primary leading-tight">{project.client}</p>
                  </div>
                </div>
                )}

                {/* Timeline */}
                {project.duration && (
                <div className="group relative bg-surface/40 backdrop-blur-xl rounded-2xl p-6 border border-white/5 hover:border-accent/30 hover:bg-surface/60 transition-all duration-300 hover:shadow-[0_8px_30px_rgba(0,195,255,0.08)] flex gap-5 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10 p-3.5 bg-[#1E293B]/80 rounded-xl h-fit border border-white/10 shadow-inner group-hover:bg-accent/20 group-hover:border-accent/30 group-hover:scale-105 transition-all duration-300">
                    <Icon name="Clock" size={22} className="text-accent group-hover:text-accent-light drop-shadow-[0_0_10px_rgba(0,195,255,0.4)] transition-colors" />
                  </div>
                  <div className="relative z-10 flex flex-col justify-center">
                    <h3 className="text-[11px] font-black text-text-muted mb-1.5 tracking-[0.2em] font-heading group-hover:text-accent/80 transition-colors uppercase">Timeline</h3>
                    <p className="text-base font-bold text-text-primary leading-tight">{project.duration}</p>
                  </div>
                </div>
                )}

                {/* Key Metric */}
                {project.metric && (
                <div className="group relative bg-surface/40 backdrop-blur-xl rounded-2xl p-6 border border-white/5 hover:border-accent/30 hover:bg-surface/60 transition-all duration-300 hover:shadow-[0_8px_30px_rgba(0,195,255,0.08)] flex gap-5 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10 p-3.5 bg-[#1E293B]/80 rounded-xl h-fit border border-white/10 shadow-inner group-hover:bg-accent/20 group-hover:border-accent/30 group-hover:scale-105 transition-all duration-300">
                    <Icon name="TrendingUp" size={22} className="text-accent group-hover:text-accent-light drop-shadow-[0_0_10px_rgba(0,195,255,0.4)] transition-colors" />
                  </div>
                  <div className="relative z-10 flex flex-col justify-center">
                    <h3 className="text-[11px] font-black text-text-muted mb-1.5 tracking-[0.2em] font-heading group-hover:text-accent/80 transition-colors uppercase">Key Metric</h3>
                    <p className="text-base font-bold text-text-primary leading-tight">{project.metric}</p>
                  </div>
                </div>
                )}

                {/* Tools Used (Spans Full Width) */}
                <div className="md:col-span-2 lg:col-span-3 group relative bg-surface/40 backdrop-blur-xl rounded-2xl p-6 border border-white/5 hover:border-accent/30 hover:bg-surface/60 transition-all duration-300 hover:shadow-[0_8px_30px_rgba(0,195,255,0.08)] flex flex-col sm:flex-row sm:items-center gap-5 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-accent/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10 flex gap-5 items-center sm:pr-8 sm:border-r border-white/10 min-w-fit">
                    <div className="p-3.5 bg-[#1E293B]/80 rounded-xl h-fit border border-white/10 shadow-inner group-hover:bg-accent/20 group-hover:border-accent/30 group-hover:scale-105 transition-all duration-300">
                      <Icon name="LayoutGrid" size={22} className="text-accent group-hover:text-accent-light drop-shadow-[0_0_10px_rgba(0,195,255,0.4)] transition-colors" />
                    </div>
                    <div className="flex flex-col justify-center">
                      <h3 className="text-[11px] font-black text-text-muted mb-0.5 tracking-[0.2em] font-heading group-hover:text-accent/80 transition-colors uppercase">Tools Used</h3>
                    </div>
                  </div>
                  
                  <div className="relative z-10 flex-1 flex flex-wrap gap-2.5 items-center sm:pl-2">
                     {project.toolsUsed?.map((tool, idx) => (
                       <span key={idx} className="px-3.5 py-1.5 bg-white/5 rounded-lg text-sm font-semibold text-text-secondary border border-white/10 hover:border-accent/50 hover:text-white hover:bg-accent/10 transition-all duration-300 shadow-sm cursor-default">
                         {tool}
                       </span>
                     ))}
                  </div>
                </div>
              </div>

              {/* Intro Text */}
              <p className="text-xl sm:text-2xl font-medium text-text-secondary italic mb-8 border-l-4 border-accent pl-6 leading-relaxed">
                {project.description}
              </p>

              <hr className="border-border-muted mb-12" />

              {/* Detailed Content */}
              <div className="prose prose-invert prose-slate prose-lg max-w-none text-text-secondary leading-relaxed space-y-6 lg:prose-xl">
               <div 
                 className="project-content"
                 dangerouslySetInnerHTML={{ __html: sanitizeAndFormat(project.detailedContent, 'mb-6') }} 
               />
              </div>

              {project.services?.length > 0 && (
                <div className="mt-12">
                  <h3 className="text-2xl font-bold mb-4 font-heading text-text-primary">Services & Deliverables</h3>
                  <div className="flex flex-wrap gap-3">
                    {project.services.map((service, idx) => (
                      <span key={idx} className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-text-secondary font-medium shadow-sm">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {project.results?.length > 0 && (
                <div className="mt-12 bg-success/10 border border-success/20 rounded-2xl p-8 shadow-inner">
                  <h3 className="text-2xl font-bold mb-6 font-heading text-success-light flex items-center gap-2">
                    <Icon name="TrendingUp" className="text-success-light" />
                    Key Results & Impact
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {project.results.map((result, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <Icon name="CheckCircle" size={20} className="text-success-light shrink-0 mt-0.5" />
                        <span className="text-text-primary font-medium">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Section */}
              <div className="mt-16 pt-12 border-t border-border-muted flex flex-wrap gap-4">
                {project.liveUrl && (
                  <Button 
                    variant="primary" 
                    size="lg"
                    className="w-full sm:w-fit py-4 px-10 text-lg font-bold bg-accent hover:bg-accent-600 text-white shadow-lg shadow-accent/20"
                    onClick={() => window.open(project.liveUrl, '_blank')}
                    title={`Open external Live Demo for ${project.title}`}
                    aria-label={`View live deployed application or site for ${project.title}`}
                  >
                    View Live Project
                    <Icon name="ExternalLink" size={20} className="ml-2" />
                  </Button>
                )}
                
                {project.codeUrl && (
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="w-full sm:w-fit py-4 px-10 text-lg font-bold border-white/20 hover:bg-white/5"
                    onClick={() => window.open(project.codeUrl, '_blank')}
                    title={`Open Source Code repository for ${project.title}`}
                    aria-label={`View source code repository for ${project.title}`}
                  >
                    View Source Code
                    <Icon name="Github" size={20} className="ml-2" />
                  </Button>
                )}
              </div>

            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProjectDetail;
